import dayjs from "dayjs";
import lodash from "lodash";
import config from "../config/config";
import { IUser } from "../domain/user/user";
import { isLoginRoute } from "./constants";

export interface IAuthConfig {
  expirationKey: string;
  tokenKey: string;
  rolesKey: string;
}

export interface ITokenClaims {
  /** Issued-at, seconds since epoch. */
  iat: number;
  /** Expiry, seconds since epoch. */
  exp: number;
  /** Login the token was issued for. */
  email?: string;
}

export enum LoginOperationResult {
  Success = 200,
  WrongLoginData = 401,
  NoContracts = 403,
  TooManyRequest = 429,
  UnexpectedError = 500,
}

export interface ILoginResult {
  result: LoginOperationResult;
  token?: string;
  user?: IUser;
}

const { auth } = config;
const { expirationKey, tokenKey, rolesKey } = auth as IAuthConfig;

export enum StoreKeys {
  TOKEN = "wbt:token",
  /** Where to send the user back to after a forced re-login. Session-scoped, see `storeReturnUrl`. */
  RETURN_URL = "wbt:next",
}

/**
 * A remembered location and the login it belongs to. Both carriers use this shape.
 *
 * `nextOwner` is absent when no session was interrupted — a deep link into a protected
 * route — and such an entry is honoured for whoever logs in.
 */
export interface IReturnUrl {
  next?: string;
  nextOwner?: string;
}

/**
 * Remembers where the user was, so a forced logout can return them there.
 *
 * `sessionStorage`, not router state: the 401 interceptor leaves via a full page
 * load, which discards history state entirely. Per-tab scope is the right scope —
 * a return URL means nothing in a tab the user never got bounced out of.
 *
 * Callers must not store the login page; `resolveReturnUrl` rejects it as well.
 */
export const storeReturnUrl = (returnUrl: IReturnUrl): void => {
  try {
    sessionStorage.setItem(StoreKeys.RETURN_URL, JSON.stringify(returnUrl));
  } catch {
    // Ignore sessionStorage errors (disabled, quota, private browsing) — losing the
    // return URL degrades to the default landing page, which is not worth failing on.
  }
};

/** Forgets a pending return URL — only an explicit logout does this, a forced one wants it kept. */
export const clearReturnUrl = (): void => {
  try {
    sessionStorage.removeItem(StoreKeys.RETURN_URL);
  } catch {
    // See storeReturnUrl.
  }
};

/**
 * Decides where to send the user after a successful login, consuming whatever was
 * remembered on the way in.
 *
 * `routerState` wins when present: it was set by the redirect that just happened,
 * while storage only ever holds the survivor of an earlier full reload. The stored
 * value is cleared either way — leaving it behind would let a 401 from one journey
 * hijack an unrelated login later in the same tab.
 *
 * Returns `undefined` for anything the caller must not navigate to, leaving it to
 * fall back to its own default:
 * - not root-relative, or root-relative but resolving to another origin, which would
 *   turn this into an open redirect;
 * - the login page itself, which would bounce straight back here;
 * - a location remembered for another login, such as a second user on a shared
 *   workstation or the admin behind an expired impersonation.
 */
export const resolveReturnUrl = (
  loggedInEmail: string | undefined,
  routerState?: IReturnUrl | null,
): string | undefined => {
  const stored = readStoredReturnUrl();
  const { next, nextOwner } = (routerState?.next ? routerState : stored) ?? {};

  if (!next || !isSafeReturnUrl(next)) {
    return undefined;
  }

  return !nextOwner || isSameLogin(nextOwner, loggedInEmail) ? next : undefined;
};

/** Reads and consumes the stored entry; anything unparsable is dropped. */
const readStoredReturnUrl = (): IReturnUrl | undefined => {
  let raw: string | null = null;
  try {
    raw = sessionStorage.getItem(StoreKeys.RETURN_URL);
    sessionStorage.removeItem(StoreKeys.RETURN_URL);
  } catch {
    // Ignore sessionStorage errors and fall through to the router-state value.
  }

  try {
    const { next, nextOwner } = raw ? JSON.parse(raw) : {};

    return typeof next === "string"
      ? { next, nextOwner: typeof nextOwner === "string" ? nextOwner : undefined }
      : undefined;
  } catch {
    return undefined;
  }
};

const isSameLogin = (owner: string, loggedInEmail?: string): boolean =>
  !!loggedInEmail && owner.toLowerCase() === loggedInEmail.toLowerCase();

const isSafeReturnUrl = (url: string): boolean => {
  // Relative paths resolve against the current directory, so origin alone would pass them.
  if (!url.startsWith("/")) {
    return false;
  }

  try {
    const target = new URL(url, window.location.origin);

    // The parser, not a string check: it also folds `/\\host` and tab-separated variants
    // onto another origin, which any hand-written guard keeps missing.
    return target.origin === window.location.origin && !isLoginRoute(target.pathname);
  } catch {
    return false;
  }
};

export const isAuthExpired = () => {
  if (typeof Storage !== "undefined") {
    try {
      const timeStamp = localStorage.getItem(expirationKey);
      const expiresAt = dayjs(Number(timeStamp));
      if (!timeStamp) {
        clearLogin();
        return true;
      }

      const isExpired = dayjs().isAfter(expiresAt);
      if (isExpired) {
        clearLogin();
        return true;
      }
      return false;
    } catch {
      return true;
    }
  } else {
    return false;
  }
};

/**
 * Reads `iat`, `exp` and `email` out of a JWT's payload without verifying the
 * signature — the backend does that; here they only decide when the session expires
 * locally, when the keepalive should fire, and whose session it was.
 *
 * Returns null for anything malformed so callers can fail closed. That matters:
 * a NaN expiry would flow into `dayjs(NaN).isAfter()`, which is always false,
 * and the session would then never appear expired.
 */
export const decodeToken = (token: string): ITokenClaims | null => {
  try {
    const payload = token.split(".")[1];
    if (!payload) {
      return null;
    }

    const json = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    const { iat, exp, email } = JSON.parse(json);
    if (typeof iat !== "number" || typeof exp !== "number") {
      return null;
    }

    return { iat, exp, email: typeof email === "string" ? email : undefined };
  } catch {
    return null;
  }
};

/**
 * The single place a token is written to storage.
 *
 * Expiry is derived from the token's own `exp` rather than a hardcoded duration,
 * so the two can never drift and a change to the backend's JWT_EXPIRATION needs
 * no frontend release. Every caller that stores a token — login, login-as,
 * logout-as and the renewal interceptor — must go through here.
 */
export const setSession = (token: string): void => {
  const claims = decodeToken(token);
  if (!claims) {
    clearLogin();
    return;
  }

  try {
    localStorage.setItem(tokenKey, token);
    localStorage.setItem(expirationKey, String(claims.exp * 1000));
  } catch {
    // Ignore localStorage errors (e.g. quota exceeded, disabled, etc.)
  }
};

/** Whose session the stored token belongs to; undefined when there is no readable token. */
export const getSessionEmail = (): string | undefined => {
  const token = localStorage.getItem(tokenKey);

  return token ? decodeToken(token)?.email : undefined;
};

export const clearLogin = () => {
  localStorage.removeItem(expirationKey);
  localStorage.removeItem(tokenKey);
  localStorage.removeItem(rolesKey);
  localStorage.removeItem("paths");
  localStorage.removeItem("auth");
  localStorage.removeItem("simple_values");
  localStorage.removeItem("locale");
};

export const isUserLoggedIn = () => {
  return !lodash.isNil(localStorage.getItem(StoreKeys.TOKEN)) && !isAuthExpired();
};
